import React, { useMemo } from 'react';
import { Button } from '../../';
import { Table } from 'antd';
import { createPreflightComponentColumns } from '../utils';
import { PreflightChecksTable } from './PreflightChecksTable';
import './PreflightComponent.scss';

/**
 * Individual preflight component (matches Backbone preflight_component_template.hbs)
 * @param {Object} component - Preflight component data
 * @param {boolean} inspectSourcesMode - Whether in source inspection mode
 * @param {boolean} allComplete - Whether all preflights are complete
 * @param {Set} expandedComponents - Set of expanded component IDs
 * @param {Array} selectedRowKeys - Array of selected component IDs
 * @param {Map} nonErrorsHiddenPerComponent - Map of component visibility states
 * @param {Function} onComponentExpand - Callback for expanding/collapsing component
 * @param {Function} onRowSelection - Callback for selecting/deselecting component
 * @param {Function} onViewAllToggle - Callback for toggling view all checks
 * @param {Function} onRecoveryChange - Callback for recovery state changes
 * @param {Function} getRecoveryState - Function to get recovery state
 * @param {Function} onRestart - Callback for restarting preflight check
 * @param {Object} migrationContext - Migration context containing alternate name/type configurations
 * @param {Object} [migrationContext.alternateType] - Type display overrides (e.g., {'source_config': 'Target VM Config'})
 * @param {Object} [migrationContext.alternateName] - Name resolution configurations (e.g., {'source_config': {path: 'target_config.properties.name'}})
 * @param {Array} [migrationContext.mandatoryTypes] - Array of mandatory preflight types
 * @param {boolean} [migrationContext.proceedOnSuccess] - Whether to proceed automatically on success
 * @param {boolean} [migrationContext.dataOnlyMigration] - Whether this is a data-only migration
 * @param {boolean} [migrationContext.vdiMigration] - Whether this is a VDI migration
 */
export const PreflightComponent = ({
    component,
    inspectSourcesMode,
    allComplete,
    expandedComponents,
    selectedRowKeys,
    nonErrorsHiddenPerComponent,
    onComponentExpand,
    onRowSelection,
    onViewAllToggle,
    onRecoveryChange,
    getRecoveryState,
    onRestart,
    migrationContext,
}) => {
    const isExpanded = expandedComponents.has(component.resource_id);
    // Determine if restart button should be shown
    const shouldShowRestart =
        !!inspectSourcesMode && allComplete && component.state === 'error' && component.errorType !== 'warning'; // Fatal error (not just warning)

    const columns = createPreflightComponentColumns({
        inspectSourcesMode,
        allComplete,
        expandedComponents,
        selectedRowKeys,
        onComponentExpand,
        onRowSelection,
        migrationContext,
    });

    // Determine if preflight checks table should be visible
    const showPreflightChecksTable = useMemo(() => {
        return (
            allComplete &&
            component.data?.content &&
            (isExpanded || inspectSourcesMode) &&
            !(inspectSourcesMode && component.state === 'success')
        );
    }, [allComplete, component.data?.content, component.state, isExpanded, inspectSourcesMode]);

    return (
        <div
            key={component.resource_id}
            className="preflight-component preflight-body panel panel-default"
            data-resource-id={component.resource_id}
        >
            <Table
                size="small"
                pagination={false}
                showHeader={false}
                dataSource={[component]}
                columns={columns}
                rowKey="resource_id"
            />

            {showPreflightChecksTable && (
                <div className="preflights padding-top-10">
                    <PreflightChecksTable
                        checks={component.data.content}
                        componentId={component.resource_id}
                        inspectSourcesMode={inspectSourcesMode}
                        nonErrorsHiddenPerComponent={nonErrorsHiddenPerComponent}
                        onRecoveryChange={onRecoveryChange}
                        getRecoveryState={getRecoveryState}
                    />

                    {/* View all checks link - only show in migration readiness mode */}
                    {!inspectSourcesMode && (
                        <div className="view-all-toggle">
                            <a onClick={() => onViewAllToggle(component.resource_id)} className="toggle-link">
                                {nonErrorsHiddenPerComponent.get(component.resource_id) === true
                                    ? 'View all checks'
                                    : 'View only checks with errors and warnings'}
                            </a>
                        </div>
                    )}
                </div>
            )}

            {shouldShowRestart && onRestart && (
                <div>
                    <Button type="default" className="restart-button" onClick={() => onRestart(component.resource_id)}>
                        Recheck this source
                    </Button>
                </div>
            )}
        </div>
    );
};
